import React, { useState } from 'react';
import { logger } from '@/utils/logger';
import { useFinanceTransactions, useCreateFinanceTransaction, useDeleteFinanceTransaction } from '@/hooks/usePSI';
import {
  IFinanceTransaction,
  IFinanceCreateRequest,
  FinanceTransactionType,
  CASH_IN_CATEGORIES,
  CASH_OUT_CATEGORIES,
  PAYMENT_METHODS,
} from '@/types/psi';
import SearchBar from '@/components/SearchBar';
import Pagination from '@/components/Pagination';
import LoadingSpinner from '@/components/LoadingSpinner';

const FinanceList: React.FC = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [transactionType, setTransactionType] = useState<FinanceTransactionType>('cash_in');
  const [newTransaction, setNewTransaction] = useState<Partial<IFinanceCreateRequest>>({
    type: 'cash_in',
    date: new Date().toISOString().split('T')[0],
    status: 'pending',
  });

  const { data, isLoading, refetch } = useFinanceTransactions({ page, pageSize, q: searchQuery });
  const createMutation = useCreateFinanceTransaction();
  const deleteMutation = useDeleteFinanceTransaction();

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPage(1);
  };

  const handleStartAdd = (type: FinanceTransactionType) => {
    setTransactionType(type);
    setNewTransaction({
      type,
      date: new Date().toISOString().split('T')[0],
      status: 'pending',
      amount: 0,
      category: '',
      description: '',
    });
    setIsAddingNew(true);
  };

  const handleAddNew = async () => {
    if (!newTransaction.amount || !newTransaction.category || !newTransaction.description) {
      alert('Please fill in amount, category, and description');
      return;
    }

    try {
      await createMutation.mutateAsync({
        ...newTransaction,
        type: transactionType,
      } as IFinanceCreateRequest);
      setIsAddingNew(false);
      setNewTransaction({});
    } catch (error) {
      logger.error('Failed to create transaction:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this transaction?')) {
      try {
        await deleteMutation.mutateAsync(id);
      } catch (error) {
        logger.error('Failed to delete transaction:', error);
      }
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getTypeBadge = (type: FinanceTransactionType) => {
    return type === 'cash_in'
      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200'
      : 'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200';
  };

  const categories = transactionType === 'cash_in' ? CASH_IN_CATEGORIES : CASH_OUT_CATEGORIES;

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg p-4 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-100 text-sm">Total Cash In</p>
              <p className="text-2xl font-bold">{formatCurrency(data?.summary?.total_cash_in || 0)}</p>
            </div>
            <div className="bg-white/20 p-3 rounded-full">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-rose-500 to-rose-600 rounded-lg p-4 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-rose-100 text-sm">Total Cash Out</p>
              <p className="text-2xl font-bold">{formatCurrency(data?.summary?.total_cash_out || 0)}</p>
            </div>
            <div className="bg-white/20 p-3 rounded-full">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
              </svg>
            </div>
          </div>
        </div>

        <div className={`bg-gradient-to-br ${(data?.summary?.net_balance || 0) >= 0 ? 'from-blue-500 to-blue-600' : 'from-orange-500 to-orange-600'} rounded-lg p-4 text-white shadow-lg`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Net Balance</p>
              <p className="text-2xl font-bold">{formatCurrency(data?.summary?.net_balance || 0)}</p>
            </div>
            <div className="bg-white/20 p-3 rounded-full">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-3">
        <button
          onClick={() => handleStartAdd('cash_in')}
          className="flex items-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors shadow-md"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span className="font-medium">Cash In</span>
        </button>
        <button
          onClick={() => handleStartAdd('cash_out')}
          className="flex items-center gap-2 px-4 py-3 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors shadow-md"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
          </svg>
          <span className="font-medium">Cash Out</span>
        </button>
      </div>

      {/* Add Transaction Form */}
      {isAddingNew && (
        <div className={`rounded-lg p-4 border-2 ${transactionType === 'cash_in' ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-700' : 'bg-rose-50 dark:bg-rose-900/30 border-rose-200 dark:border-rose-700'}`}>
          <h3 className="text-lg font-medium mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
            {transactionType === 'cash_in' ? (
              <>
                <span className="w-3 h-3 bg-emerald-500 rounded-full"></span>
                New Cash In
              </>
            ) : (
              <>
                <span className="w-3 h-3 bg-rose-500 rounded-full"></span>
                New Cash Out
              </>
            )}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date</label>
              <input
                type="date"
                value={newTransaction.date || ''}
                onChange={(e) => setNewTransaction({ ...newTransaction, date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount</label>
              <input
                type="number"
                placeholder="0"
                value={newTransaction.amount || ''}
                onChange={(e) => setNewTransaction({ ...newTransaction, amount: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
              <select
                value={newTransaction.category || ''}
                onChange={(e) => setNewTransaction({ ...newTransaction, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Select category...</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
              <input
                type="text"
                placeholder="Transaction description"
                value={newTransaction.description || ''}
                onChange={(e) => setNewTransaction({ ...newTransaction, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Payment Method</label>
              <select
                value={newTransaction.payment_method || ''}
                onChange={(e) => setNewTransaction({ ...newTransaction, payment_method: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Select method...</option>
                {PAYMENT_METHODS.map((method) => (
                  <option key={method} value={method}>{method}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reference</label>
              <input
                type="text"
                placeholder="Reference number"
                value={newTransaction.reference || ''}
                onChange={(e) => setNewTransaction({ ...newTransaction, reference: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
              <input
                type="text"
                placeholder="Additional notes"
                value={newTransaction.notes || ''}
                onChange={(e) => setNewTransaction({ ...newTransaction, notes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => setIsAddingNew(false)}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAddNew}
              disabled={createMutation.isLoading}
              className={`px-4 py-2 text-white rounded-lg transition-colors ${
                transactionType === 'cash_in'
                  ? 'bg-emerald-600 hover:bg-emerald-700'
                  : 'bg-rose-600 hover:bg-rose-700'
              } disabled:opacity-50`}
            >
              {createMutation.isLoading ? 'Saving...' : 'Save Transaction'}
            </button>
          </div>
        </div>
      )}

      {/* Search and Controls */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <SearchBar
          value={searchQuery}
          onChange={handleSearch}
          placeholder="Search transactions..."
        />
        <button
          onClick={() => refetch()}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
        >
          Refresh
        </button>
      </div>

      {/* Transactions Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">ID</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Amount</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Category</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Description</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Payment</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {data?.transactions?.map((transaction: IFinanceTransaction) => (
              <tr key={transaction.transaction_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white">{transaction.transaction_id}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getTypeBadge(transaction.type)}`}>
                    {transaction.type === 'cash_in' ? 'Cash In' : 'Cash Out'}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{transaction.date}</td>
                <td className={`px-4 py-3 text-sm font-medium ${transaction.type === 'cash_in' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                  {transaction.type === 'cash_in' ? '+' : '-'}{formatCurrency(transaction.amount)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{transaction.category}</td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white max-w-xs truncate" title={transaction.description}>
                  {transaction.description}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{transaction.payment_method || '-'}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(transaction.status)}`}>
                    {transaction.status === 'completed' ? 'Completed' : transaction.status === 'pending' ? 'Pending' : 'Cancelled'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => handleDelete(transaction.transaction_id)}
                    disabled={deleteMutation.isLoading}
                    className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {(!data?.transactions || data.transactions.length === 0) && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                  No transactions found. Click "Cash In" or "Cash Out" to add one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data && data.total_pages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={data.total_pages}
          onPageChange={setPage}
        />
      )}

      {/* Summary */}
      <div className="text-sm text-gray-500 dark:text-gray-400">
        Showing {data?.transactions?.length || 0} of {data?.total_count || 0} transactions
      </div>
    </div>
  );
};

export default FinanceList;
